import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Checkbox } from "../components/ui/checkbox";
import { Search, CalendarDays, Filter, X } from "lucide-react";

const PAGE_SIZE = 30;

// Human-readable labels for audit actions
const ACTION_LABELS = {
    login: "Inició sesión",
    logout: "Cerró sesión",
    create: "Creó",
    update: "Actualizó",
    delete: "Eliminó",
    bulk_create: "Importó en lote",
    bulk_delete: "Eliminó en lote",
    delete_all: "Eliminó todos",
    import: "Importó datos",
    export: "Exportó datos",
    connect_google_calendar: "Conectó Google Calendar",
    disconnect_google_calendar: "Desconectó Google Calendar",
    role_change: "Cambió rol",
    permission_change: "Cambió permisos",
    convert: "Convirtió",
    move: "Movió",
};

const RESOURCE_LABELS = {
    auth: "su cuenta",
    lead: "un contacto",
    company: "una empresa",
    deal: "un deal",
    funnel: "un embudo",
    task: "una tarea",
    user: "un usuario",
    role: "un rol",
    calendar: "el calendario",
    calendar_event: "un evento de calendario",
};

// Events offered in the multi-select
const EVENT_OPTIONS = [
    "login", "logout",
    "create", "update", "delete",
    "bulk_create", "bulk_delete",
    "import", "export",
    "role_change", "permission_change",
    "connect_google_calendar", "disconnect_google_calendar",
];

function describe(log) {
    const action = ACTION_LABELS[log.action] || log.action || "acción";
    const resource = RESOURCE_LABELS[log.resource] || log.resource || "un recurso";
    const meta = log.meta || {};
    let extra = "";
    if (typeof meta.deleted === "number") extra = ` (${meta.deleted} registros)`;
    else if (typeof meta.inserted === "number") extra = ` (${meta.inserted} insertados, ${meta.skipped || 0} omitidos)`;
    if (log.action === "login") return "Inició sesión";
    if (log.action === "logout") return "Cerró sesión";
    return `${action} ${resource}${extra}`;
}

const fmtDay = (d) => d ? d.toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" }) : null;

const fmtRange = (range) => {
    if (!range?.from) return "Cualquier fecha";
    if (!range.to || range.from.toDateString() === range.to.toDateString()) return fmtDay(range.from);
    return `${fmtDay(range.from)} – ${fmtDay(range.to)}`;
};

export default function AuditLogs() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [selectedRange, setSelectedRange] = useState({ from: undefined, to: undefined });
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
            if (q.trim()) params.q = q.trim();
            if (selectedEvents.length > 0) params.actions = selectedEvents.join(",");
            if (selectedRange?.from) {
                const start = new Date(selectedRange.from); start.setHours(0,0,0,0);
                const endBase = selectedRange.to || selectedRange.from;
                const end = new Date(endBase); end.setHours(23,59,59,999);
                params.date_from = start.toISOString();
                params.date_to = end.toISOString();
            }
            const { data } = await api.get("/admin/audit", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally { setLoading(false); }
    }, [q, page, selectedRange, selectedEvents]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [q, selectedRange, selectedEvents]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const toggleEvent = (ev) => {
        setSelectedEvents((prev) => prev.includes(ev) ? prev.filter(e => e !== ev) : [...prev, ev]);
    };

    const clearAll = () => { setQ(""); setSelectedRange({ from: undefined, to: undefined }); setSelectedEvents([]); };

    const hasFilters = q.trim() || selectedRange?.from || selectedEvents.length > 0;

    return (
        <Layout title="Logs de Auditoría" subtitle="Actividad del sistema">
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {/* Filters bar */}
                <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-slate-400"/>
                        <Input
                            value={q}
                            onChange={(e)=>setQ(e.target.value)}
                            placeholder="Buscar por usuario (prefijo)..."
                            className="border-0 focus-visible:ring-0 shadow-none"
                            data-testid="audit-search-input"
                        />
                    </div>

                    {/* Date range picker */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="audit-date-button" className={selectedRange?.from ? "border-orange-400 text-orange-700" : ""}>
                                <CalendarDays className="w-4 h-4 mr-2"/>{fmtRange(selectedRange)}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                            <Calendar
                                mode="range"
                                selected={selectedRange}
                                onSelect={(r)=>setSelectedRange(r || { from: undefined, to: undefined })}
                                numberOfMonths={1}
                                data-testid="audit-date-calendar"
                            />
                            {selectedRange?.from && (
                                <div className="p-2 border-t border-slate-100 flex justify-end">
                                    <Button variant="ghost" size="sm" onClick={()=>setSelectedRange({ from: undefined, to: undefined })} data-testid="audit-date-clear">
                                        <X className="w-3.5 h-3.5 mr-1"/>Limpiar fecha
                                    </Button>
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>

                    {/* Event multi-select */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="audit-event-button" className={selectedEvents.length > 0 ? "border-orange-400 text-orange-700" : ""}>
                                <Filter className="w-4 h-4 mr-2"/>
                                {selectedEvents.length === 0 ? "Todos los eventos" : `${selectedEvents.length} evento(s)`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-2" align="end">
                            <div className="text-xs text-slate-500 px-2 py-1.5 uppercase tracking-wider font-semibold">Eventos (OR)</div>
                            <div className="max-h-72 overflow-y-auto" data-testid="audit-event-list">
                                {EVENT_OPTIONS.map((ev) => (
                                    <label key={ev} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer">
                                        <Checkbox
                                            checked={selectedEvents.includes(ev)}
                                            onCheckedChange={()=>toggleEvent(ev)}
                                            data-testid={`audit-event-${ev}`}
                                        />
                                        <span className="text-sm flex-1">{ACTION_LABELS[ev] || ev}</span>
                                        <span className="text-[10px] text-slate-400 font-mono">{ev}</span>
                                    </label>
                                ))}
                            </div>
                            {selectedEvents.length > 0 && (
                                <div className="border-t border-slate-100 mt-2 pt-2 flex justify-end">
                                    <Button variant="ghost" size="sm" onClick={()=>setSelectedEvents([])} data-testid="audit-event-clear">
                                        <X className="w-3.5 h-3.5 mr-1"/>Quitar todos
                                    </Button>
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>

                    {hasFilters && (
                        <Button variant="ghost" size="sm" onClick={clearAll} data-testid="audit-clear-all" className="text-slate-500 hover:text-orange-600">
                            Limpiar filtros
                        </Button>
                    )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto scrollbar-thin">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Usuario</th>
                                <th className="text-left px-4 py-3">Evento</th>
                                <th className="text-left px-4 py-3">Referencia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((l, i) => (
                                <tr key={l.log_id || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                                    <td className="px-4 py-3 text-slate-600 font-mono text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-slate-700">
                                        <div className="text-slate-900">{l.user_name || "—"}</div>
                                        <div className="text-xs text-slate-400">{l.user_email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-slate-800">{describe(l)}</div>
                                        <div className="text-[10px] text-slate-400 font-mono">{l.action}</div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{l.resource_id && l.resource_id !== "*" ? l.resource_id : "—"}</td>
                                </tr>
                            ))}
                            {!loading && items.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-400">Sin actividad para los filtros aplicados</td></tr>}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total > 0 && (
                    <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm" data-testid="audit-pagination">
                        <div className="text-slate-500">
                            Mostrando {(page-1)*PAGE_SIZE + 1}–{Math.min(page*PAGE_SIZE, total)} de {total}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} data-testid="audit-page-prev">Anterior</Button>
                            <span className="text-slate-600 font-mono text-xs px-2">Página {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.min(totalPages, p+1))} disabled={page>=totalPages} data-testid="audit-page-next">Siguiente</Button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
